import { motion } from "framer-motion";
import { Calendar, MapPin, Image } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { getOptimizedImageUrl, getThumbnailUrl } from "@/utils/imageUtils";
import { cn } from "@/lib/utils";
import type { EventWithImages } from "@/hooks/useGalleryEvents";

interface EventCardProps {
  event: EventWithImages;
  language: string;
  onClick: () => void;
  priority?: boolean;
}

const EventCard = ({ event, language, onClick, priority }: EventCardProps) => {
  const title = language === 'KO' && event.title_ko ? event.title_ko : event.title_en;
  const description = language === 'KO' && event.description_ko ? event.description_ko : event.description_en;

  // Format the date
  const formattedDate = format(new Date(event.event_date), 'MMM d, yyyy');

  return (
    <div
      className="group cursor-pointer h-full"
      onClick={onClick}
    >
      <div className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 h-full hover:shadow-lg hover:translate-y-[-4px] flex flex-col">
        <div className="relative w-full pt-[56.25%] bg-gray-100"> {/* 16:9 aspect ratio */}
          {event.coverImage ? (
            <>
              <img
                src={getThumbnailUrl(event.coverImage.image_url)}
                alt={event.coverImage.alt_text || title}
                className={cn(
                  "absolute top-0 left-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105",
                  "opacity-0"
                )}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                decoding="async"
                onLoad={(e) => e.currentTarget.classList.replace("opacity-0", "opacity-100")}
                onError={(e) => {
                  const img = e.currentTarget;
                  const stage = img.dataset.fallback;
                  if (!stage) {
                    img.dataset.fallback = "render";
                    img.src = getOptimizedImageUrl(event.coverImage!.image_url, { width: 600, quality: 80 });
                  } else if (stage === "render") {
                    img.dataset.fallback = "original";
                    img.src = event.coverImage!.image_url;
                  }
                }}
              />
              {event.imageCount && event.imageCount > 0 && (
                <div className="absolute bottom-3 right-3">
                  <Badge variant="secondary" className="flex items-center gap-1 bg-black/70 text-white">
                    <Image className="w-4 h-4" />
                    {event.imageCount}
                  </Badge>
                </div>
              )}
            </>
          ) : (
            <div className="absolute top-0 left-0 w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">No image available</span>
            </div>
          )}
        </div>
        
        <div className="p-6 flex flex-col flex-grow">
          <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 min-h-[3.5rem]">
            {title}
          </h3>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 line-clamp-3 flex-grow">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventCard;